/**
 * Service Layer - Drawing Service
 * Handles drawing tools logic (marker, beacon, draw)
 * Uses Core layer for canvas operations
 * Updates State layer (store)
 */

import {v4 as uuidv4} from "uuid";
import {CanvasUtils} from '../core/canvas/CanvasUtils';
import {getBigCanvasRealDimensions} from '../core/canvas/CanvasTransform';
import {commandTypeEnum, markerTypeEnum, messageTypeEnum} from '../core/constants/RtcEnums';

export class DrawingService {
    constructor(store, socketManager) {
        this.store = store;
        this.socketManager = socketManager;
    }

    /**
     * Add arrow marker to canvas
     */
    addArrowMarker(context, x, y, color, isReverse = false) {
        CanvasUtils.arrow(context, x, y, color, false, isReverse);
    }

    /**
     * Add beacon (circle) to canvas
     */
    addBeacon(context, x, y, color) {
        CanvasUtils.circle(context, x, y, color);
    }

    /**
     * Draw a line on canvas
     */
    drawLine(context, fromX, fromY, toX, toY, color) {
        context.strokeStyle = color;
        context.beginPath();
        context.moveTo(fromX, fromY);
        context.lineTo(toX, toY);
        context.stroke();
    }

    /**
     * Add to drawing history (for undo functionality)
     */
    addToHistory(context) {
        if (!context) {
            return;
        }

        const imageData = CanvasUtils.getImageData(context);

        // Save first clean state
        if (!this.store.noDrawsCanvasImage && !this.store.remoteFreeze) {
            this.store.noDrawsCanvasImage = imageData;
        } else if (!this.store.noDrawsCanvasFrozenImage && this.store.remoteFreeze) {
            this.store.noDrawsCanvasFrozenImage = imageData;
        }

        this.store.drawsHistory.push(imageData);
    }

    /**
     * Clear all drawings
     */
    clearAll(isRemote = false) {
        const isDrawingOnSelf = this.isBigVideoSelf();

        if (isRemote || isDrawingOnSelf) {
            const noDrawsCanvasImage = this.store.noDrawsCanvasImage;
            if (!noDrawsCanvasImage) {
                return;
            }
            CanvasUtils.putImageData(this.store.drawCanvasContext, noDrawsCanvasImage);
            this.store.drawsHistory = [];
        } else {
            if (this.store.remoteFreeze) {
                const noDrawsCanvasFrozenImage = this.store.noDrawsCanvasFrozenImage;
                if (!noDrawsCanvasFrozenImage) {
                    return;
                }
                CanvasUtils.putImageData(this.store.drawCanvasContext, noDrawsCanvasFrozenImage);
                this.store.drawsHistory = [];
            }

            // Send clear command to remote
            const clearMessage = {
                Command: commandTypeEnum.CLEAR_ALL,
                MessageId: messageTypeEnum.COMMAND
            };
            this.sendDrawingCommand(clearMessage);
        }
    }

    /**
     * Clear last drawing
     */
    clearLast(isRemote = false) {
        const isDrawingOnSelf = this.isBigVideoSelf();

        if (isRemote || isDrawingOnSelf) {
            const previousCanvasImage = this.store.drawsHistory.pop();
            this.store.drawsHistory.pop();

            if (!previousCanvasImage) {
                this.clearAll(isRemote);
                return;
            }

            CanvasUtils.putImageData(this.store.drawCanvasContext, previousCanvasImage);
        } else {
            if (this.store.remoteFreeze) {
                this.store.drawsHistory.pop();
                const previousCanvasImage = this.store.drawsHistory.pop();

                if (previousCanvasImage) {
                    CanvasUtils.putImageData(this.store.drawCanvasContext, previousCanvasImage);
                } else {
                    this.clearAll(isRemote);
                    return;
                }
            }

            // Send cancel command to remote
            const cancelMessage = {
                Command: commandTypeEnum.CLEAR_LAST,
                MessageId: messageTypeEnum.COMMAND
            };
            this.sendDrawingCommand(cancelMessage);
        }
    }

    /**
     * Handle drawing or marking on own canvas
     */
    drawOnSelf(canvasX, canvasY, toolType, color) {
        const context = this.store.drawCanvasContext;
        const rect = this.store.bigCanvasRef.current.getBoundingClientRect();

        let selfX = canvasX;
        let selfY = canvasY;

        // Adjust for black stripes if drawing on frozen remote
        if (!this.isBigVideoSelf()) {
            const dimensions = getBigCanvasRealDimensions(this.store.bigCanvasRef);
            const {blackStripeHeight, blackStripeWidth, actualHeight, actualWidth} = dimensions;

            if (selfX < blackStripeWidth || selfY < blackStripeHeight ||
                selfX > blackStripeWidth + actualWidth || selfY > blackStripeHeight + actualHeight) {
                return;
            }

            selfX = (selfX - blackStripeWidth) * (2 * blackStripeWidth + actualWidth) / actualWidth;
            selfY = (selfY - blackStripeHeight) * (2 * blackStripeHeight + actualHeight) / actualHeight;
        }

        const x = this.getCorrectedX(context, selfX * context.canvas.width / rect.width, false, true);
        const y = selfY * context.canvas.height / rect.height;

        // Handle different tool types
        if (toolType === 'draw') {
            this.addDrawSelf(context, x, y, color);
        } else if (toolType === 'marker') {
            this.addToHistory(context);
            this.addArrowMarker(context, x, y, color, !this.isBigVideoSelf());
        } else if (toolType === 'beacon') {
            this.addToHistory(context);
            this.addBeacon(context, x, y, color);
        }
    }

    /**
     * Add drawing stroke to self
     */
    addDrawSelf(context, x, y, color) {
        if (this.store.drawPosition && this.store.drawPosition.id === this.store.drawPositionId) {
            this.drawLine(context, this.store.drawPosition.x, this.store.drawPosition.y, x, y, color);
        } else {
            this.addToHistory(context);
        }

        this.store.drawPosition = {
            id: this.store.drawPositionId,
            x,
            y
        };
    }

    /**
     * Send drawing or marker to other participants
     */
    drawOrMarkToOthers(canvasX, canvasY, markerType) {
        let {x, y} = this.correctBlackStripes(canvasX, canvasY);

        // Adjust coordinates for AR/screen share
        if (this.store.frozenFrame && this.isDrawOnAR()) {
            y = this.isHoloLens() ? y : 1 - y;
            x = 1 - x;
        }

        const isScreenShare = this.store.latestFramesObj[this.store.biggerWindowConnectionId]?.IsScreenShare;
        if (isScreenShare) {
            x = 1 - x;
        }

        // Validate coordinates
        if (x < 0 || x > 1 || y < 0 || y > 1) {
            return;
        }

        const position = {x, y, z: 0};

        // Convert color to normalized RGB
        const colorArray = this.store.selectedToolColor.replace('rgb(', '').replace(')', '').split(',');
        const color = {
            x: colorArray[0] / 255,
            y: colorArray[1] / 255,
            z: colorArray[2] / 255,
        };

        const messageId = markerType === markerTypeEnum.DRAW ? messageTypeEnum.DRAW_MARKER : messageTypeEnum.MARKER;
        const drawMessage = {
            MarkerColor: color,
            MarkerId: markerType === markerTypeEnum.DRAW ? this.store.drawPositionId : uuidv4(),
            RayOutPos: position,
            MarkerType: markerType,
            MessageId: messageId,
        };

        if (this.store.frozenFrame) {
            drawMessage.CamFrameInformation = this.store.frozenFrame;
        }

        this.sendDrawingCommand(drawMessage);
    }

    /**
     * Handle received drawing from remote
     */
    addDrawFromRemote(content, isSenderWeb) {
        const callback = (x, y, rgbColor) => {
            x = this.getCorrectedX(this.store.drawCanvasContext, x, isSenderWeb, false);

            if (this.store.drawPosition && this.store.drawPosition.id === content.MarkerId) {
                const {r, g, b} = rgbColor;
                this.drawLine(
                    this.store.drawCanvasContext,
                    this.store.drawPosition.x,
                    this.store.drawPosition.y,
                    x,
                    y,
                    `rgb(${r}, ${g}, ${b})`
                );
            } else {
                this.addToHistory(this.store.drawCanvasContext);
            }

            this.store.drawPosition = {
                id: content.MarkerId,
                x,
                y
            };
        };

        this.processDrawingContent(content, callback);
    }

    /**
     * Handle received marker from remote
     */
    addMarkerFromRemote(content, isSenderWeb) {
        let drawFunc;

        switch (content.MarkerType) {
            case markerTypeEnum.BEACON:
                drawFunc = this.addBeacon.bind(this);
                break;
            case markerTypeEnum.ARROW:
                drawFunc = this.addArrowMarker.bind(this);
                break;
            default:
                console.log('unrecognized marker', content);
                return;
        }

        const callback = (x, y, rgbColor) => {
            this.addToHistory(this.store.drawCanvasContext);
            const {r, g, b} = rgbColor;
            const color = `rgb(${r}, ${g}, ${b})`;
            x = this.getCorrectedX(this.store.drawCanvasContext, x, isSenderWeb, false);
            drawFunc(this.store.drawCanvasContext, x, y, color);
        };

        this.processDrawingContent(content, callback);
    }

    /**
     * Process drawing content (common logic for markers and draws)
     */
    processDrawingContent(content, callback) {
        if (!content.MarkerColor || !content.RayOutPos || !content.MarkerId) {
            console.error('Invalid drawing content');
            return;
        }

        const r = 255 * content.MarkerColor.x;
        const g = 255 * content.MarkerColor.y;
        const b = 255 * content.MarkerColor.z;
        const x = this.store.drawCanvasContext.canvas.width * content.RayOutPos.x;

        let y = this.store.drawCanvasContext.canvas.height * content.RayOutPos.y;
        if (this.store.shareScreenOn) {
            y = this.store.drawCanvasContext.canvas.height - y;
        }

        callback(x, y, {r, g, b});
    }

    /**
     * Send drawing command via socket
     */
    sendDrawingCommand(drawMessage) {
        const data = JSON.stringify(drawMessage);
        // IMPORTANT: Use deviceId (sender's ID), not biggerWindowConnectionId (receiver's ID)
        this.socketManager.sendRoomMessage(this.store.roomId, data, this.store.deviceId);
    }

    /**
     * Send freeze command to remote
     */
    sendFreezeToRemote() {
        const freezeMessage = {
            Command: commandTypeEnum.FREEZE,
            MessageId: messageTypeEnum.COMMAND,
            TargetDeviceId: this.store.biggerWindowConnectionId  // Target specific participant
        };
        this.sendDrawingCommand(freezeMessage);
    }

    /**
     * Finish drawing on remote (unfreeze)
     */
    finishDrawingOnRemote() {
        this.store.noDrawsCanvasFrozenImage = null;
        CanvasUtils.clearCanvas(this.store.drawCanvasContext);
        this.store.frozenFrame = null;
    }

    /**
     * Get corrected X coordinate for flipping
     */
    getCorrectedX(context, x, isSenderWeb, isSelf) {
        if (this.store.remoteFreeze && !this.isBigVideoSelf()) {
            return x;
        }
        if (this.store.facingMode === 'environment' && isSelf) {
            return x;
        }
        if (this.store.shareScreenOn && isSenderWeb) {
            return x;
        }
        if (this.store.shareScreenOn && isSelf) {
            return x;
        }
        return context.canvas.width - x;
    }

    /**
     * Correct coordinates accounting for black stripes
     */
    correctBlackStripes(canvasX, canvasY) {
        const dimensions = getBigCanvasRealDimensions(this.store.bigCanvasRef);
        const {blackStripeHeight, blackStripeWidth, actualHeight, actualWidth} = dimensions;

        const x = 1 - (canvasX - blackStripeWidth) / actualWidth;
        const y = 1 - (canvasY - blackStripeHeight) / actualHeight;

        return {x, y};
    }

    /**
     * Check if drawing on AR device
     */
    isDrawOnAR() {
        const drawingDeviceFirstFrame = this.store.latestFramesObj[this.store.biggerWindowConnectionId];
        if (!drawingDeviceFirstFrame) {
            return false;
        }
        return ['HL2', 'ML2', 'IOS', 'Android'].includes(drawingDeviceFirstFrame.DeviceName);
    }

    /**
     * Check if device is HoloLens
     */
    isHoloLens(senderId) {
        const id = senderId || this.store.biggerWindowConnectionId;
        if (!id) {
            return false;
        }
        const frameInfo = this.store.latestFramesObj[id];
        return frameInfo && ['HL2', 'ML2'].includes(frameInfo.DeviceName);
    }

    /**
     * Check if big video is self
     */
    isBigVideoSelf() {
        return this.store.biggerWindowConnectionId === this.store.deviceId;
    }
}
